import React, { useState, useEffect, useRef } from "react";
import { CircleStyle, NavStyle } from "./NavBar.styled";
import ROUTE from "../../common/Routes";
import Color from "../../common/ColorEnum";
import TextComponent from "../Text/TextComponent";
import { useReload } from "../../context/ReloadContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  setEmail,
  setId,
  setNavigateTo,
  setSideBar,
  setTitle,
  setPreviousNode,
} from "../../redux/NavigateData/navigateSlice";
import { useDB } from "../../context/DynamoDBContext";
import {
  getTreeData1API,
  getTreeData2API,
  getTreeData3API,
} from "../../api/NavBarApi";
import {
  setExpandedNodesRed,
  setSortType,
  setTreeDataRed,
} from "../../redux/TreeData/TreeDataSlice";
import moment from "moment";
import SortIcon from "./SortIcon";

const NavBar = () => {
  const { reload } = useReload();
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const { dynamoDBClient } = useDB();
  const queryClient = useQueryClient();
  const treeDataRed = useSelector((state) => state.treeDataRed);

  const [searchData, setSearchData] = useState("");
  const [searchFilter, setSearchFilter] = useState(1);
  const [showSidebar, setShowSidebar] = useState(true);
  const [filterdData, setFilterdData] = useState(treeDataRed.treeData);
  const [enableSearch, setEnableSearch] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [refreshData2, setRefreshData2] = useState(false);
  const [refreshData3, setRefreshData3] = useState(false);
  const [treeReload, setTreeReload] = useState(reload);
  const [status, setStatus] = useState("Loading . . .");

  useEffect(() => {
    if (dynamoDBClient !== null) {
      if (treeDataRed.treeData.length === 0) {
        setRefreshData(true);
        setTreeReload(true);
      }
    }
  }, [dynamoDBClient]);

  const handleError = () => {
    setRefreshData(false);
    setRefreshData3(false);
    setRefreshData2(false);
    setStatus("Error has Occured. Reload");
  };

  const { status: treeData1Status, data: treeData1 } = useQuery({
    queryKey: ["treeData1"],
    queryFn: () => getTreeData1API(dynamoDBClient, treeDataRed.sortType),
    enabled: refreshData,
    onSuccess: (data) => {
      setRefreshData(false);
      setRefreshData2(true);
      queryClient.fetchQuery(["treeData2"], () =>
        getTreeData2API(dynamoDBClient, data)
      );
    },
    onError: (error) => {
      console.log("tree data 1 error:", error);
      handleError("treeData1");
    },
  });
  const { status: treeData2Status, data: treeData2 } = useQuery({
    queryKey: ["treeData2"],
    queryFn: () => getTreeData2API(dynamoDBClient, treeData1),
    enabled: refreshData2,
    onSuccess: (data) => {
      setRefreshData2(false);
      setRefreshData3(true);
      queryClient.fetchQuery(["treeData3"], () =>
        getTreeData3API(dynamoDBClient, data)
      );
    },
    onError: (error) => {
      console.log("tree data 2 error:", error);
      handleError("treeData2");
    },
  });

  const { status: treeData3Status, data: treeData3 } = useQuery({
    queryKey: ["treeData3"],
    queryFn: () => getTreeData3API(dynamoDBClient, treeData2),
    enabled: refreshData3,
    onSuccess: (data) => {
      setRefreshData3(false);
      dispatch(setTreeDataRed(data));
      setTreeReload(false);
    },
    onError: (error) => {
      console.log("tree data 3 error:", error);
      handleError("treeData3");
    },
  });

  const reloadTree = async () => {
    setRefreshData(true);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowSidebar(true);
        dispatch(setSideBar(true));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function handleNodeClick(node) {
    reloadTree();
    // Navigate to the node's URL if it exists

    if (node.navigateTo === 0) {
      dispatch(setNavigateTo(node.navigateTo));
    } else if (node.navigateTo === 5) {
      // Do nothing because it does not exist
    } else {
      dispatch(setId(node.id));
      dispatch(setEmail(node.email));
      dispatch(setTitle(node.name));
      dispatch(setNavigateTo(node.navigateTo));
      dispatch(setPreviousNode(node));
    }

    // Expand or collapse the node if it has children
    if (node.children) {
      dispatch(setExpandedNodesRed(node.id));
    }
  }

  function calculateColor(node) {
    if (!node.children || node.color === Color.offline) {
      return node.color;
    }

    let hasRedChild = false;
    let hasYellowChild = false;

    for (const child of node.children) {
      if (child.color === Color.offline) {
        hasRedChild = true;
      } else if (child.color === Color.snoozed) {
        hasYellowChild = true;
      }
    }

    if (hasRedChild) {
      return Color.orange;
    }

    if (hasYellowChild) {
      return Color.snoozed;
    }

    return node.color;
  }

  const searchByBeacon = (searchTerm) => {
    setEnableSearch(true);
    if (searchTerm === "") {
      setFilterdData([]);
      return;
    }
    const searchTxt = searchTerm.toLowerCase();
    const filteredArray = treeDataRed.treeData.reduce((acc, obj) => {
      const accounts = obj.children;
      if (accounts) {
        for (const obj2 of accounts) {
          const baseStations = obj2.children;
          if (baseStations) {
            for (const obj3 of baseStations) {
              const beacons = obj3.children;
              for (const child of beacons) {
                if (
                  child.id.toLowerCase().includes(searchTxt) ||
                  child.name.toLowerCase().includes(searchTxt)
                ) {
                  acc.push(child);
                }
              }
            }
          }
        }
      }

      return acc;
    }, []);
    setFilterdData(filteredArray);
  };

  const searchByAccount = (searchTerm) => {
    setEnableSearch(true);
    if (searchTerm === "") {
      setFilterdData([]);
      return;
    }
    const searchTxt = searchTerm.toLowerCase();
    const filteredArray = treeDataRed.treeData.reduce((acc, obj) => {
      const children = obj.children;
      if (children) {
        for (const child of children) {
          if (
            child.id.toLowerCase().includes(searchTxt) ||
            child.name.toLowerCase().includes(searchTxt)
          ) {
            acc.push(child);
          }
        }
      }

      return acc;
    }, []);
    setFilterdData(filteredArray);
  };
  const searchByBaseStation = (searchTerm) => {
    setEnableSearch(true);
    if (searchTerm === "") {
      setFilterdData([]);
      return;
    }
    const searchTxt = searchTerm.toLowerCase();
    const filteredArray = treeDataRed.treeData.reduce((acc, obj) => {
      const accounts = obj.children;
      if (accounts) {
        for (const obj2 of accounts) {
          const baseStations = obj2.children;
          if (baseStations) {
            for (const child of baseStations) {
              if (
                child.id.toLowerCase().includes(searchTxt) ||
                child.name.toLowerCase().includes(searchTxt)
              ) {
                acc.push(child);
              }
            }
          }
        }
      }

      return acc;
    }, []);
    setFilterdData(filteredArray);
  };

  const searchToPerform = (type) => {
    if (type === 1) {
      searchByAccount(searchData);
    }
    if (type === 2) {
      searchByBaseStation(searchData);
    }
    if (type === 3) {
      searchByBeacon(searchData);
    }
  };

  const performSearch = (type) => {
    setSearchFilter(type);
    if (searchData !== "") {
      searchToPerform(type);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key == "Enter") {
      setEnableSearch(true);
      searchToPerform(searchFilter);
    }
  };

  function renderTreeNode(node) {
    const isExpanded = treeDataRed.expandedNodes[node.id];
    const nodeColor = calculateColor(node);
    return (
      <div key={node.id} data-id={node.id} className="tree-main">
        <div className="node-div" onClick={() => handleNodeClick(node)}>
          <div className="div1">
            <div className="sign">
              {node.children ? (isExpanded ? "-" : "+") : ""}
            </div>
            <p className="text-node">
              <TextComponent text={node.name} limit={node.limit} />
            </p>
          </div>
          <div className="div2">
            <CircleStyle
              color={nodeColor}
              display={node.color === "none" ? "none" : "inline-block"}
            />
          </div>
        </div>
        {node.children && isExpanded && (
          <div style={{ paddingLeft: "30px" }}>
            {node?.children?.map(renderTreeNode)}
          </div>
        )}
      </div>
    );
  }

  const sorting = async (val) => {
    let sortedData = [];
    sortedData = [...(treeDataRed.treeData[0]?.children || [])];

    if (val === "a-z") {
      sortedData.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      dispatch(setSortType("a-z"));
    }
    if (val === "z-a") {
      sortedData.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
      dispatch(setSortType("z-a"));
    }
    if (val === "dateAsc") {
      sortedData.sort((a, b) => {
        const dateA = moment(a.date, "MM/DD/YYYY hh:mm:ss a");
        const dateB = moment(b.date, "MM/DD/YYYY hh:mm:ss a");
        return dateB - dateA;
      });
      dispatch(setSortType("dateAsc"));
    }
    if (val === "dateDesc") {
      sortedData.sort((a, b) => {
        const dateA = moment(a.date, "MM/DD/YYYY hh:mm:ss a");
        const dateB = moment(b.date, "MM/DD/YYYY hh:mm:ss a");
        return dateA - dateB;
      });
      dispatch(setSortType("dateDesc"));
    }

    dispatch(
      setTreeDataRed([
        {
          id: "All",
          name: "All",
          navigate: ROUTE.Home,
          navigateTo: 0,
          color: "none",
          children: sortedData,
        },
      ])
    );
  };

  return (
    <NavStyle>
      {showSidebar ? (
        <div
          className="hamburger"
          onClick={() => {
            setShowSidebar(!showSidebar);
            dispatch(setSideBar(!showSidebar));
          }}
        >
          <i className="bi bi-x-lg crossStyle"></i>
        </div>
      ) : (
        <div
          className="hamburger"
          onClick={() => {
            setShowSidebar(!showSidebar);
            dispatch(setSideBar(!showSidebar));
          }}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      )}

      {showSidebar && (
        <nav
          className={showSidebar ? `left-sidebar open` : "left-sidebar"}
          ref={scrollRef}
        >
          <div className="filter-div">
            <p className="searchtxt">Filter :</p>
            <div
              className="btn-group btn-group-sm btn-div"
              role="group"
              aria-label="Basic example"
            >
              <button
                type="button"
                className={
                  searchFilter === 1
                    ? "btn btn-secondary btn-active-div filter-btn"
                    : "btn btn-secondary filter-btn"
                }
                onClick={() => performSearch(1)}
              >
                Account
              </button>
              <button
                type="button"
                className={
                  searchFilter === 2
                    ? "btn btn-secondary btn-active-div filter-btn"
                    : "btn btn-secondary filter-btn"
                }
                onClick={() => performSearch(2)}
              >
                Base Station
              </button>
              <button
                type="button"
                className={
                  searchFilter === 3
                    ? "btn btn-secondary btn-active-div filter-btn"
                    : "btn btn-secondary filter-btn"
                }
                onClick={() => performSearch(3)}
              >
                Beacon
              </button>
            </div>
          </div>
          <div className="search-bar">
            <input
              className="search-input"
              type="text"
              placeholder="Search"
              value={searchData}
              onChange={(e) => {
                setSearchData(e.target.value);
                setEnableSearch(false);
              }}
              onKeyUp={handleKeyPress}
            />
            {enableSearch ? (
              <i
                className="bi bi-x search-icon"
                onClick={() => {
                  setSearchData("");
                  setEnableSearch(false);
                }}
              ></i>
            ) : (
              <i
                className="bi bi-search search-icon"
                onClick={() => {
                  searchToPerform(searchFilter);
                }}
              ></i>
            )}
          </div>
          <div className="sort-div">
            {treeReload || reload ? (
              <p>{status}</p>
            ) : enableSearch ? (
              <>
                {filterdData.length > 0 ? (
                  <div className="tree-main2">
                    {filterdData?.map(renderTreeNode)}
                  </div>
                ) : (
                  <p>No Result found</p>
                )}
              </>
            ) : (
              treeDataRed.treeData?.map(renderTreeNode)
            )}
            {!enableSearch && (
              <div className="dropdown">
                <button
                  className="btn btn-secondary btn-sort"
                  type="button"
                  title="Sort"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <SortIcon />
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button
                      className={
                        treeDataRed.sortType === "a-z"
                          ? "selected-sort dropdown-item"
                          : "dropdown-item"
                      }
                      type="button"
                      onClick={() => sorting("a-z")}
                    >
                      A-Z
                    </button>
                  </li>
                  <li>
                    <button
                      className={
                        treeDataRed.sortType === "z-a"
                          ? "selected-sort dropdown-item"
                          : "dropdown-item"
                      }
                      type="button"
                      onClick={() => sorting("z-a")}
                    >
                      Z-A
                    </button>
                  </li>
                  <li>
                    <button
                      className={
                        treeDataRed.sortType === "dateAsc"
                          ? "selected-sort dropdown-item"
                          : "dropdown-item"
                      }
                      type="button"
                      onClick={() => sorting("dateAsc")}
                    >
                      Date Created Asc
                    </button>
                  </li>
                  <li>
                    <button
                      className={
                        treeDataRed.sortType === "dateDesc"
                          ? "selected-sort dropdown-item"
                          : "dropdown-item"
                      }
                      type="button"
                      onClick={() => sorting("dateDesc")}
                    >
                      Date Created Desc
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </nav>
      )}
    </NavStyle>
  );
};

export default NavBar;
